import { useEffect, useMemo, useRef, useState } from 'react';
import { getLogoCliente } from '../services/clienteService';
import { getAssinaturaUsuario, getUser } from '../services/usersService';

// Busca a assinatura de um usuário (data URI) com cache por id no `cacheRef` da página.
function useAssinaturaUsuario(idUsuario, cacheRef, setPendentes) {
  const [assinatura, setAssinatura] = useState(null);

  useEffect(() => {
    let ativo = true;
    if (!idUsuario) { setAssinatura(null); return undefined; }
    const cache = cacheRef.current.usuario;
    if (Object.prototype.hasOwnProperty.call(cache, idUsuario)) {
      setAssinatura(cache[idUsuario]);
      return undefined;
    }
    setPendentes((n) => n + 1);
    getAssinaturaUsuario(idUsuario)
      .then((res) => {
        const uri = (res && res.dataUri) || null;
        cache[idUsuario] = uri;
        if (ativo) setAssinatura(uri);
      })
      .catch(() => { if (ativo) setAssinatura(null); })
      .finally(() => { setPendentes((n) => n - 1); });
    return () => { ativo = false; };
  }, [idUsuario, cacheRef, setPendentes]);

  return assinatura;
}

// Busca as imagens personalizáveis dos relatórios (logo do cliente e assinaturas dos dois usuários
// vinculados no cadastro da empresa) já como data URI base64, prontas para o <Image> do
// @react-pdf/renderer. Cada recurso é buscado uma vez por id e mantido em cache enquanto a página
// vive, para não rebuscar a cada mudança de filtro.
export default function usePersonalizacaoRelatorio({ idUsuario1, idUsuario2 }) {
  const [logoCliente, setLogoCliente] = useState(null);
  const [nomesUsuarios, setNomesUsuarios] = useState({});
  const [pendentes, setPendentes] = useState(0);

  const cacheRef = useRef({ logo: undefined, usuario: {} });

  // Carrega o logo do cliente (uma única vez).
  useEffect(() => {
    let ativo = true;
    if (cacheRef.current.logo !== undefined) {
      setLogoCliente(cacheRef.current.logo);
      return undefined;
    }
    setPendentes((n) => n + 1);
    getLogoCliente()
      .then((res) => {
        const uri = (res && res.dataUri) || null;
        cacheRef.current.logo = uri;
        if (ativo) setLogoCliente(uri);
      })
      .catch(() => { if (ativo) setLogoCliente(null); })
      .finally(() => { setPendentes((n) => n - 1); });
    return () => { ativo = false; };
  }, []);

  // Carrega os nomes dos usuários (uma única vez) para a legenda das assinaturas.
  useEffect(() => {
    let ativo = true;
    setPendentes((n) => n + 1);
    getUser()
      .then((data) => {
        if (!ativo || !Array.isArray(data)) return;
        const nomes = {};
        data.forEach((u) => {
          const id = String(u.idUsuario || u.id || '');
          if (id) nomes[id] = u.nomeCompleto || u.nome || '';
        });
        setNomesUsuarios(nomes);
      })
      .catch(() => {})
      .finally(() => { setPendentes((n) => n - 1); });
    return () => { ativo = false; };
  }, []);

  const assinatura1 = useAssinaturaUsuario(idUsuario1, cacheRef, setPendentes);
  const assinatura2 = useAssinaturaUsuario(idUsuario2, cacheRef, setPendentes);

  const assinaturas = useMemo(() => [
    { src: assinatura1, nome: idUsuario1 ? nomesUsuarios[String(idUsuario1)] || '' : '' },
    { src: assinatura2, nome: idUsuario2 ? nomesUsuarios[String(idUsuario2)] || '' : '' },
  ], [assinatura1, assinatura2, idUsuario1, idUsuario2, nomesUsuarios]);

  return {
    logoCliente,
    assinaturas,
    carregando: pendentes > 0,
  };
}
